import type {
  AssistantAccommodationOfferPageSubmitPayload,
  AssistantCreateMessageRequest,
  AssistantSnapshotCreateMessageRequest,
  AssistantSnapshotSubmitPayload,
  AssistantSubmitPayload,
  AssistantSubmitCreateMessageRequest,
  AssistantTripPlannerSubmitPayload,
} from './types'
import { toChatMessageSnapshotReference } from './surfaceSnapshotContract'
import { assertUnreachable } from 'lib/staticAssert/staticAssertUtils'
import { onlyDefinedProperties } from 'lib/object/objectUtils'

export function mapAssistantSubmitPayloadToCreateMessageRequest(
  payload: AssistantSubmitPayload,
): AssistantSubmitCreateMessageRequest {
  if ('snapshotRef' in payload || 'degradedSurfaceIdentity' in payload) {
    return mapSnapshotSubmitPayload(payload)
  }

  switch (payload.surface) {
    case 'trip_planner':
      return mapTripPlannerSubmitPayload(payload)
    case 'accommodation_offer_page':
      return mapAccommodationOfferPageSubmitPayload(payload)
    default:
      return assertUnreachable(payload)
  }
}

function mapSnapshotSubmitPayload(
  payload: AssistantSnapshotSubmitPayload,
): AssistantSnapshotCreateMessageRequest {
  if (payload.assistantMessageId !== undefined) {
    if (payload.snapshotRef !== undefined) {
      return {
        chatId: payload.chatId,
        content: payload.content,
        ...onlyDefinedProperties({ fileIds: payload.fileIds, files: payload.files }),
        userMessageId: payload.userMessageId,
        assistantMessageId: payload.assistantMessageId,
        userData: payload.userData,
        surface: payload.surface,
        snapshotRef: toChatMessageSnapshotReference(payload.snapshotRef),
      }
    }

    return {
      chatId: payload.chatId,
      content: payload.content,
      ...onlyDefinedProperties({ fileIds: payload.fileIds, files: payload.files }),
      userMessageId: payload.userMessageId,
      assistantMessageId: payload.assistantMessageId,
      userData: payload.userData,
      surface: payload.surface,
      degradedSurfaceIdentity: payload.degradedSurfaceIdentity,
    }
  }

  if (payload.snapshotRef !== undefined) {
    return {
      chatId: payload.chatId,
      content: payload.content,
      userMessageId: payload.userMessageId,
      regenerateMessageId: payload.regenerateMessageId,
      userData: payload.userData,
      surface: payload.surface,
      snapshotRef: toChatMessageSnapshotReference(payload.snapshotRef),
    }
  }

  return {
    chatId: payload.chatId,
    content: payload.content,
    userMessageId: payload.userMessageId,
    regenerateMessageId: payload.regenerateMessageId,
    userData: payload.userData,
    surface: payload.surface,
    degradedSurfaceIdentity: payload.degradedSurfaceIdentity,
  }
}

function mapTripPlannerSubmitPayload(
  payload: AssistantTripPlannerSubmitPayload,
): AssistantCreateMessageRequest {
  if (payload.assistantMessageId !== undefined) {
    return {
      chatId: payload.chatId,
      content: payload.content,
      ...onlyDefinedProperties({ fileIds: payload.fileIds, files: payload.files }),
      userMessageId: payload.userMessageId,
      assistantMessageId: payload.assistantMessageId,
      userData: payload.userData,
      surface: 'trip_planner',
      ...(payload.prompt !== undefined ? { prompt: payload.prompt } : {}),
    }
  }

  return {
    chatId: payload.chatId,
    content: payload.content,
    userMessageId: payload.userMessageId,
    regenerateMessageId: payload.regenerateMessageId,
    userData: payload.userData,
    surface: 'trip_planner',
    ...(payload.prompt !== undefined ? { prompt: payload.prompt } : {}),
  }
}

function mapAccommodationOfferPageSubmitPayload(
  payload: AssistantAccommodationOfferPageSubmitPayload,
): AssistantCreateMessageRequest {
  if (payload.assistantMessageId !== undefined) {
    return {
      chatId: payload.chatId,
      content: payload.content,
      ...onlyDefinedProperties({ fileIds: payload.fileIds, files: payload.files }),
      userMessageId: payload.userMessageId,
      assistantMessageId: payload.assistantMessageId,
      userData: payload.userData,
      surface: 'accommodation_offer_page',
      surfaceContext: payload.surfaceContext,
      ...(payload.prompt !== undefined ? { prompt: payload.prompt } : {}),
    }
  }

  return {
    chatId: payload.chatId,
    content: payload.content,
    userMessageId: payload.userMessageId,
    regenerateMessageId: payload.regenerateMessageId,
    userData: payload.userData,
    surface: 'accommodation_offer_page',
    surfaceContext: payload.surfaceContext,
    ...(payload.prompt !== undefined ? { prompt: payload.prompt } : {}),
  }
}
